/**
 * Finnhub-Provider.
 * Spricht ausschließlich mit unseren Netlify-Functions
 * (/api/finnhub/quote, /search, /profile). Der Finnhub-API-Key bleibt damit
 * server-seitig.
 *
 * Wenn die Function 503 'no_api_key' zurückgibt, mappen wir das als
 * status='no_data' (= "Provider nicht konfiguriert") statt als Fehler — sonst
 * würde die UI rote Error-Cards zeigen, obwohl es nur eine Konfigurations-
 * lücke ist.
 */

import { DataStateHelpers } from '@/types';
import type { AssetClass, DataState } from '@/types';
import type {
  MarketDataProvider,
  OhlcvBar,
  OhlcvTimeframe,
  PriceQuote,
  SymbolLookupResult,
} from './types';

interface FinnhubQuoteResponse {
  c?: number; // current price
  d?: number; // change
  dp?: number; // change percent
  h?: number; // intraday high
  l?: number; // intraday low
  o?: number; // open
  pc?: number; // previous close
  t?: number; // timestamp
}

interface FinnhubSearchResultItem {
  description?: string;
  displaySymbol?: string;
  symbol?: string;
  type?: string;
}

interface FinnhubSearchResponse {
  count?: number;
  result?: FinnhubSearchResultItem[];
}

interface FinnhubProfileResponse {
  symbol: string;
  profile?: {
    name?: string;
    exchange?: string;
    currency?: string;
    marketCapitalization?: number;
    finnhubIndustry?: string;
  };
  metric?: {
    '52WeekHigh'?: number;
    '52WeekLow'?: number;
    peTTM?: number;
    pbAnnual?: number;
    roeTTM?: number;
    '5DayPriceReturnDaily'?: number;
    '13WeekPriceReturnDaily'?: number;
    '26WeekPriceReturnDaily'?: number;
    '52WeekPriceReturnDaily'?: number;
    [key: string]: unknown;
  };
}

async function fetchJson<T>(
  url: string,
  signal: AbortSignal | undefined
): Promise<{ ok: true; data: T } | { ok: false; status: number; body: unknown }> {
  const res = await fetch(url, signal ? { signal } : {});
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return { ok: false, status: res.status, body };
  }
  const data = (await res.json()) as T;
  return { ok: true, data };
}

function classifyType(finnhubType?: string): AssetClass {
  const t = (finnhubType ?? '').toLowerCase();
  if (t.includes('etf')) return 'etf';
  if (t.includes('crypto')) return 'crypto';
  // Common stock / preferred / equity
  return 'stock';
}

export const FINNHUB_PROVIDER: MarketDataProvider = {
  name: 'Finnhub',
  is_live: true,

  async lookupSymbol(query, signal): Promise<DataState<SymbolLookupResult[]>> {
    const trimmed = query.trim();
    if (trimmed.length === 0) return DataStateHelpers.noData();

    const result = await fetchJson<FinnhubSearchResponse>(
      `/api/finnhub/search?q=${encodeURIComponent(trimmed)}`,
      signal
    );

    if (!result.ok) {
      if (result.status === 503) return DataStateHelpers.noData();
      return DataStateHelpers.error({
        code: `HTTP_${result.status}`,
        message: `Symbol-Suche fehlgeschlagen (Status ${result.status}).`,
        retry_possible: result.status >= 500,
      });
    }

    const items = (result.data.result ?? [])
      .filter((r) => r.symbol)
      .map<SymbolLookupResult>((r) => ({
        symbol: (r.displaySymbol ?? r.symbol ?? '').toUpperCase(),
        display_name: r.description ?? r.symbol ?? '',
        asset_class: classifyType(r.type),
        exchange: '',
        currency: '',
      }));

    if (items.length === 0) return DataStateHelpers.noData();
    return DataStateHelpers.success(items, 300);
  },

  async getQuote(symbol, signal): Promise<DataState<PriceQuote>> {
    const symU = symbol.toUpperCase();
    const [quoteResult, profileResult] = await Promise.all([
      fetchJson<FinnhubQuoteResponse>(
        `/api/finnhub/quote?symbol=${encodeURIComponent(symU)}`,
        signal
      ),
      fetchJson<FinnhubProfileResponse>(
        `/api/finnhub/profile?symbol=${encodeURIComponent(symU)}`,
        signal
      ),
    ]);

    if (!quoteResult.ok) {
      if (quoteResult.status === 503) return DataStateHelpers.noData();
      return DataStateHelpers.error({
        code: `HTTP_${quoteResult.status}`,
        message: `Quote-Anfrage fehlgeschlagen (Status ${quoteResult.status}).`,
        retry_possible: quoteResult.status >= 500,
      });
    }

    const q = quoteResult.data;
    const profile = profileResult.ok ? profileResult.data : null;
    const metric = profile?.metric ?? {};
    const profileObj = profile?.profile ?? {};

    const currentPrice = typeof q.c === 'number' ? q.c : 0;
    if (currentPrice === 0) {
      // Finnhub liefert für unbekannte / nicht-supportete Symbole alle Felder = 0
      return DataStateHelpers.noData();
    }

    const high52 = typeof metric['52WeekHigh'] === 'number' ? (metric['52WeekHigh'] as number) : 0;
    const low52 = typeof metric['52WeekLow'] === 'number' ? (metric['52WeekLow'] as number) : 0;
    const marketCapBn =
      typeof profileObj.marketCapitalization === 'number'
        ? profileObj.marketCapitalization / 1000 // Finnhub liefert in Mio
        : null;
    const pe = typeof metric.peTTM === 'number' ? (metric.peTTM as number) : null;

    const numOrNull = (key: string): number | null =>
      typeof metric[key] === 'number' ? (metric[key] as number) : null;

    const drawdown =
      high52 > 0 ? ((currentPrice - high52) / high52) * 100 : null;

    return DataStateHelpers.success(
      {
        symbol: symU,
        current_price: currentPrice,
        change_pct_today: typeof q.dp === 'number' ? q.dp : 0,
        high_52w: high52,
        low_52w: low52,
        market_cap_billions: marketCapBn,
        pe_ttm: pe,
        fetched_at: new Date().toISOString(),
        return_5d_pct: numOrNull('5DayPriceReturnDaily'),
        return_13w_pct: numOrNull('13WeekPriceReturnDaily'),
        return_26w_pct: numOrNull('26WeekPriceReturnDaily'),
        return_52w_pct: numOrNull('52WeekPriceReturnDaily'),
        roe_ttm_pct: numOrNull('roeTTM'),
        drawdown_from_52w_high_pct: drawdown,
        industry: profileObj.finnhubIndustry ?? null,
      },
      60
    );
  },

  async getOhlcv(
    _symbol: string,
    _timeframe: OhlcvTimeframe,
    _bars: number,
    _signal?: AbortSignal
  ): Promise<DataState<OhlcvBar[]>> {
    // Finnhub Free hat keine /stock/candle mehr (nur Premium).
    // Für OHLCV brauchen wir später TwelveData oder IBKR.
    return DataStateHelpers.error({
      code: 'NOT_IMPLEMENTED',
      message: 'OHLCV-Abruf via Finnhub Free nicht verfügbar — kommt mit TwelveData oder IBKR.',
      retry_possible: false,
    });
  },
};
